// Shared read-only organisation chart; HR can edit people and job roles from Admin.

import { useState, type FormEvent } from "react";
import { useCtx, type Ctx } from "../store/useCtx";
import { useStore } from "../store/StoreProvider";
import { managerIds, teamHeads, titleOf } from "../lib/orgchart";
import type { User } from "../types";
import { Avatar, byName, plural } from "../ui/bits";
import { CommitInput } from "../ui/CommitInput";
import { useDialogs } from "../ui/dialogs";
import { Icon } from "../ui/Icon";
import { useRun, useToast } from "../ui/Toasts";

export function OrgChartPage() {
  const ctx = useCtx();
  return <OrgChart ctx={ctx} />;
}

export function OrgChart({ ctx, onEdit }: { ctx: Ctx; onEdit?: (person: User) => void }) {
  const edit = ctx.me.role === "hr" ? onEdit : undefined;
  const managers = managerIds(ctx.users);
  const heads = teamHeads(ctx.users);
  const active = ctx.users.filter((u) => u.active);
  const hr = active.filter((u) => u.role === "hr").sort(byName);
  const staff = active.filter((u) => u.role !== "hr");
  const nameOf = (id: string | undefined) => (id ? ctx.userById.get(id)?.name : undefined);

  // One card per department, plus one for people who are in none.
  const teams = [
    ...ctx.departments
      .slice()
      .sort((a, b) => a.name.localeCompare(b.name, "en-GB"))
      .map((d) => ({ id: d.id as string | null, name: d.name })),
    { id: null, name: "No department" },
  ]
    .map((t) => ({ ...t, members: staff.filter((u) => (u.departmentId ?? null) === t.id) }))
    .filter((t) => t.id !== null || t.members.length > 0);

  const personNode = (u: User, count = 0, note = "") => {
    const dept = u.departmentId ? ctx.deptById.get(u.departmentId) : undefined;
    const job = u.jobRoleId ? ctx.jobRoles.find((j) => j.id === u.jobRoleId) : undefined;
    const content = (
      <>
        <Avatar name={u.name} photoUrl={u.photoUrl} />
        <span className="org-text">
          <span className="org-name">
            {u.name}
            {titleOf(u) !== "Employee" && <span className="chip chip-hr">{titleOf(u)}</span>}
          </span>
          <span className="org-sub">{[job?.name ?? "No job role", dept?.name].filter(Boolean).join(" · ")}</span>
          {note && <span className="org-up">{note}</span>}
        </span>
        {count > 0 && (
          <span className="org-count" title={plural(count, "direct report")}>
            <Icon name="users" size={14} />
            <span>{count}<span className="sr-only"> direct {count === 1 ? "report" : "reports"}</span></span>
          </span>
        )}
      </>
    );
    return edit ? (
      <button type="button" className="org-node" onClick={() => edit(u)} aria-label={`Edit ${u.name}`}>
        {content}
      </button>
    ) : (
      <div className="org-node">{content}</div>
    );
  };

  const team = (t: (typeof teams)[number]) => {
    const inTeam = new Set(t.members.map((u) => u.id));
    const reports = new Map<string, User[]>();
    const top: User[] = [];
    for (const u of t.members) {
      const boss = managers.get(u.id);
      if (boss && inTeam.has(boss)) reports.set(boss, [...(reports.get(boss) ?? []), u]);
      else top.push(u);
    }
    const node = (u: User, path: ReadonlySet<string>, isTop: boolean) => {
      const below = (reports.get(u.id) ?? []).slice().sort(byName);
      const next = new Set(path).add(u.id);
      // The top of a team reports to HR, unless someone outside the team was chosen for them.
      const note = isTop ? `Reports to ${nameOf(managers.get(u.id) ?? undefined) ?? "HR"}` : "";
      return (
        <li key={u.id}>
          {personNode(u, below.length, note)}
          {/* `path` stops a (data) loop from drawing forever; the app never lets one be saved. */}
          {below.length > 0 && <ul>{below.filter((b) => !next.has(b.id)).map((b) => node(b, next, false))}</ul>}
        </li>
      );
    };
    const lead = nameOf(t.id ? heads.lead.get(t.id) : undefined);
    const pm = nameOf(t.id ? heads.pm.get(t.id) : undefined);
    return (
      <section key={t.id ?? "none"} className="card org-team" aria-label={`${t.name} team`}>
        <header className="org-team-head">
          <h3>{t.name}</h3>
          <span className="muted">{plural(t.members.length, "person", "people")}</span>
        </header>
        {t.id && (
          <p className="org-team-heads">
            Product manager: <strong>{pm ?? "none"}</strong> · Team lead: <strong>{lead ?? "none"}</strong>
          </p>
        )}
        {top.length ? (
          <ul className="org-tree" aria-label={`${t.name} chart`}>
            {top.sort(byName).map((u) => node(u, new Set(), true))}
          </ul>
        ) : (
          <p className="empty">Nobody in this team yet.</p>
        )}
      </section>
    );
  };

  return (
    <>
      <p className="hint">
        Who reports to whom, team by team. It follows each team's Team lead and Product manager ticks, so it always
        matches the people.{" "}
        {edit
          ? "Click a person to change their job role, team, ticks or who they report to."
          : "This chart is read-only. HR manages it in Admin → Org chart."}{" "}
        Whoever someone reports to can approve their leave. HR are the head of everyone: the top of each team reports to
        HR unless someone else was chosen for them.
      </p>
      {active.length ? (
        <>
          {hr.length > 0 && (
            <section className="card org-hr-card" aria-label="HR">
              <header className="org-team-head">
                <h3>HR</h3>
                <span className="muted"></span>
              </header>
              <div className="org-hr-list">
                {hr.map((u) => (
                  <div key={u.id}>{personNode(u)}</div>
                ))}
              </div>
            </section>
          )}
          <div className="org-teams">{teams.map(team)}</div>
        </>
      ) : (
        <div className="card org-card">
          <p className="empty">Nobody has been added yet.</p>
        </div>
      )}
    </>
  );
}

export function JobRoles({ ctx }: { ctx: Ctx }) {
  const store = useStore();
  const run = useRun();
  const toast = useToast();
  const { confirm } = useDialogs();
  const [newName, setNewName] = useState("");
  const roles = ctx.jobRoles.slice().sort((a, b) => a.name.localeCompare(b.name, "en-GB"));

  const add = async (e: FormEvent) => {
    e.preventDefault();
    const name = newName.trim();
    if (!name) return;
    if (await run(store.saveJobRole({ name }), `${name} added.`)) setNewName("");
  };

  const remove = async (id: string) => {
    const role = ctx.jobRoles.find((j) => j.id === id)!;
    const people = ctx.users.filter((u) => u.jobRoleId === id);
    if (people.length) {
      toast(`Move ${plural(people.length, "person", "people")} out of ${role.name} first.`, "error");
      return;
    }
    const ok = await confirm({
      title: `Delete ${role.name}?`,
      message: "This cannot be undone.",
      confirmLabel: "Delete job role",
      danger: true,
    });
    if (ok) await run(store.deleteJobRole(id), "Job role deleted.");
  };

  return (
    <section aria-labelledby="jobroles-title">
      <h2 id="jobroles-title" className="section-title">
        Job roles
      </h2>
      <p className="hint">
        The list you choose from when adding someone ("Developer", "Product manager", "Director"…). Add as many as you
        need. A job role is only a label: it never changes what anyone can do.
      </p>
      <div className="card table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th scope="col">Job role</th>
              <th scope="col">People</th>
              <th scope="col">
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {roles.map((j) => (
              <tr key={j.id}>
                <td>
                  <label className="sr-only" htmlFor={`jr-${j.id}`}>
                    Name of job role {j.name}
                  </label>
                  <CommitInput
                    id={`jr-${j.id}`}
                    type="text"
                    maxLength={60}
                    value={j.name}
                    onCommit={(text) => {
                      if (!text.trim()) {
                        toast("A job role needs a name.", "error");
                        return false;
                      }
                      return run(store.saveJobRole({ id: j.id, name: text }), "Job role renamed.");
                    }}
                  />
                </td>
                <td>{ctx.users.filter((u) => u.jobRoleId === j.id).length}</td>
                <td className="right">
                  <button type="button" className="btn btn-ghost-danger btn-sm" onClick={() => remove(j.id)}>
                    <Icon name="trash" size={16} />
                    Delete
                  </button>
                </td>
              </tr>
            ))}
            {!roles.length && (
              <tr>
                <td colSpan={3} className="empty-cell">
                  No job roles yet. Add the first one below.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <form className="card add-dept" onSubmit={add}>
        <div className="field grow">
          <label htmlFor="new-job-role">Add a job role</label>
          <input
            id="new-job-role"
            type="text"
            maxLength={60}
            placeholder="e.g. Product manager"
            required
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
          />
        </div>
        <button type="submit" className="btn">
          <Icon name="plus" size={18} />
          Add
        </button>
      </form>
    </section>
  );
}
